import React, { Fragment, useState } from "react"

import Sub from "~layout/sub"
import aside from "~content/products/aside"

import { HeadingIcon } from "~styled"
import "./css/QoqoloAccordion.css"

const crumbs = [
  { text: "Products", to: "/products" },
  { text: "Qoqolo" },
]

const featureSections = [
  {
    id: "revolutionise-school-operations",
    title: "Revolutionise your Preschool Operations with Qoqolo School Management System",
    description: (
      <p>
        As your schools adapt to the new normal, Qoqolo provides a smart, intuitive, and comprehensive solution to streamline your school's operations. Our <strong>School Management System</strong> is designed to optimise administration and improve communication channels. By simplifying paperwork and other administrative tasks, Qoqolo frees up valuable bandwith, allowing your teachers, staff, and administrators to focus on what they do best - educating and nurturing students. 
      </p>
    ),
    items:[
      {
        title: "Simplify",
        description:"Qoqolo streamlines everyday administrative tasks, reducing paperwork and manual record-keeping. Teachers spend less time on routine data entry, staff can manage health records and attendance efficiently, and administrators gain a clearer overview of school operations without unnecessary complexity."
      },
      {
        title: "Analytics",
        description:"Built-in communication tools build stronger collaboration. Teachers can update parents on student progress and milestones easily, administrators can ensure consistent messaging across the school, and parents feel more connected to their child's learning journey."
      },
      {
        title: "Management",
        description:"Real-time insights help track student performance and engagement. Teachers can tailor lesson plans to individual learning needs, administrators can monitor overall academic progress, and staff can generate reports quickly for compliance or internal reviews."
      },
      {
        title: "Transformation",
        description:"Automation reduces repetitive tasks, freeing up time for meaningful work. Teachers can focus more on teaching, staff can dedicate attention to student care, and administrators can oversee strategic planning and continuous improvement with minimal operational bottlenecks."
      },
      {
        title: "Relationships",
        description:"Information is accessible anytime via mobile apps. Teachers can quickly communicate daily updates, staff can track attendance and events in real-time, and administrators can respond promptly to operational issues, keeping everyone informed and on the same page."
      },

    ]
  },
  {
    id: "student-management",
    title: "Student Management",
    description: (
      <p>
        Qoqolo's Student Management system offers a comprehensive, user-friendly platform tailored for preschools, including childcare centres and kindergartens. Designed to streamline administrative tasks, it empowers educators and administrators to focus more on nurturing young learners.
      </p>
    ),
    media: (
      <div className="productFeatures flex">
        <img src={require("~images/products/QQL/Features/1. Student Management Made Easy 1.jpg").default} alt="Student Management" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/1. Student Management Made Easy 2.jpg").default} alt="Student Management" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "Student Records",
        description:"Maintain detailed digital profiles for each child, encompassing personal information, enrollment history, and academic progress. This centralised database ensures quick access and efficient management, reducing paperwork and enhancing data accuracy."
      },
      {
        title: "Health Records",
        description:"Track and update children's health information, including allergies, medical conditions, and immunisation status. This feature supports compliance with health regulations and promotes a safe environment for all students."
      },
      {
        title: "Attendance Reports",
        description:"Monitor daily attendance with ease. Generate real-time reports to identify attendance patterns, aiding in early intervention and communication with parents."
      },
      {
        title: "Student Sign-in/Out",
        description:"Utilise a digital logbook for secure and efficient sign-in and sign-out processes. This feature enhances security and provides a reliable record of children's attendance."
      },
      {
        title: "Incident Log",
        description:"Document and track incidents promptly, ensuring that all occurrences are recorded for future reference. This log aids in maintaining a safe and responsive learning environment."
      },
      {
        title: "School Bus",
        description:"Manage transportation logistics by tracking bus schedules, routes, and student ridership. This feature ensures timely and safe transportation for children."
      },
      {
        title: "e-Prospects",
        description:"Streamline the enrollment process with digital prospect management. Provide prospective parents with easy access to information, simplifying their decision-making process."
      },
      {
        title: "eRegistration",
        description:"Facilitate online registration, allowing parents to enroll their children conveniently. This feature reduces administrative workload and accelerates the enrollment process."
      },
      {
        title: "Routine Care",
        description:"Record and monitor daily care routines, including feeding times, nap schedules, and diaper changes. This ensures consistent care and keeps parents informed about their child's day."
      }
    ]
  },
  {
    id: "parent-engagement",
    title: "Parent Engagement at Your Fingertips",
    description: (
      <p>
        Qoqolo isn't just a school management system; it's also a comprehensive parent engagement portal designed to foster meaningful connections between educators and families. By centralising communication and streamlining administrative tasks, Qoqolo empowers parents to stay informed and involved in their child's educational journey.
      </p>
    ),
    media: (
      <div className="productFeatures flex-reverse">
        <img src={require("~images/products/QQL/Features/2. Parent Engagement at Your Fingertips 1.jpg").default} alt="Parent Engagement at Your Fingertips" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/2. Parent Engagement at Your Fingertips 2.png").default} alt="Parent Engagement at Your Fingertips" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "Announcements",
        description:"Keep parents informed with timely updates on school activities, policy changes, and important notices. The announcement feature ensures that all communications are centralised and easily accessible, reducing the risk of missed information."
      },
      {
        title: "Class Space",
        description:"Each class has its own dedicated space within the portal, where teachers can share lesson plans, learning materials, and student progress. This transparency allows parents to actively participate in their child's learning process and provide support at home."
      },
      {
        title: "Calendar",
        description:"The integrated calendar feature provides a comprehensive view of school events, parent-teacher meetings, and important dates. Parents can sync these events with their personal calendars, ensuring they never miss an opportunity to engage with the school community."
      },
      {
        title: "Event Booking",
        description:"Simplify the process of attending school events with the event booking feature. Parents can easily register for upcoming activities, workshops, and conferences, facilitating better planning and participation."
      },
      {
        title: "Resource Repository",
        description:"Access a wealth of educational resources, including reading materials, activity guides, and policy documents, all in one place. The resource repository empowers parents to support their child's learning at home and stay aligned with the school's curriculum."
      }
    ]
  },
  {
    id: "student-development",
    title: "Effortlessly Track Student Development",
    description: (
      <p>
        The Qoqolo app provides educators with practical tools to monitor and document each student's learning journey, helping to support personalised development and informed teaching decisions.
      </p>
    ),
    media: (
      <div className="productFeatures flex">
        <img src={require("~images/products/QQL/Features/3. Effortlessly Track Student Development 1.png").default} alt="Effortlessly Track Student Development" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/3. Effortlessly track Student Development 2.jpg").default} alt="Effortlessly Track Student Development" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "ePortfolios",
        description:"Qoqolo's ePortfolios let educators create digital portfolios for each student, including observation notes and multimedia uploads. They streamline documentation and build meaningful parent-teacher engagement."
      },
      {
        title: "Enquiry and Reports",
        description:"The Enquiry and Reports feature allows customised report generation for individual students or groups. This helps educators track progress, identify trends, and plan teaching strategies efficiently."
      },
      {
        title: "Evaluation",
        description:"Evaluation tools support both formative and summative assessments with rubrics aligned to learning objectives. They enable timely interventions and personalised learning pathways for students."
      },
      {
        title: "Checklists",
        description:"Checklists provide a structured way to track student milestones and achievements. Educators can monitor progress across key developmental areas with clarity and consistency."
      }
    ]
  },
  {
    id: "centre-operations",
    title: "Streamline Centre Operations Management",
    description: (
      <p>
        Qoqolo's comprehensive suite of features is designed to simplify and enhance the daily operations of educational centres.
      </p>
    ),
    media: (
      <div className="productFeatures flex-reverse">
        <img src={require("~images/products/QQL/Features/4. Streamlined Centre Operations Management 1.jpg").default} alt="Streamline Centre Operations Management" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/4. Streamlined Centre Operations Management 2.png").default} alt="Streamline Centre Operations Management" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "Compliance",
        description:"Ensure adherence to regulatory standards with Qoqolo's compliance tracking tools. The system allows for the documentation and monitoring of compliance-related activities, making it easier to maintain up-to-date records and pass audits with confidence."
      },
      {
        title: "Inventory",
        description:"Manage resources effectively through Qoqolo's inventory management module. Track supplies, schedule reorders, and maintain optimal stock levels to support smooth and uninterrupted centre operations."
      },
      {
        title: "Cleaning Schedule",
        description:"Maintain a clean and safe environment with automated cleaning schedules. Assign tasks, set frequencies, and monitor completion to uphold hygiene standards across the centre."
      },
      {
        title: "SOPs",
        description:"Standardise procedures with Qoqolo's SOP management feature. Store and share standard operating procedures to ensure consistency and clarity in daily tasks and emergency protocols."
      },
      {
        title: "E-Survey",
        description:"Gather feedback and insights through integrated e-surveys. Collect data from staff, parents, and other stakeholders to inform decisions and drive continuous improvement."
      },
      {
        title: "Centre Log",
        description:"Maintain a digital logbook to record daily activities, incidents, and observations. This feature supports transparency and accountability, providing a reliable record for reference."
      },
      {
        title: "Location",
        description:"Optimise space utilisation with Qoqolo's location tracking capabilities. Assign and monitor the use of rooms and facilities, ensuring efficient scheduling and effective resource allocation"
      }
    ]
  },
  {
    id: "staff-management",
    title: "Efficient Staff Management",
    description: (
      <p>
        Managing school staff can be complex, but the Qoqolo app simplifies the process by bringing all staff-related operations into a single, intuitive platform. From attendance tracking to leave management, every function is designed to save time and reduce administrative burden, allowing schools to focus on supporting their educators and students.
      </p>
    ),
    media: (
      <div className="productFeatures flex">
        <img src={require("~images/products/QQL/Features/5. Efficient Staff Management 1.png").default} alt="Efficient Staff Management" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/5. Efficient Staff Management 2.jpg").default} alt="Efficient Staff Management" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "Staff Sign-in/Out",
        description:"Staff can log in and out digitally using mobile devices or tablets, eliminating the need for manual registers. Real-time attendance monitoring ensures accurate records and helps managers respond quickly to absences or delays."
      },
      {
        title: "Training",
        description:"Qoqolo allows schools to schedule and track staff training efficiently, keeping all professional development activities organised. Managers can monitor progress and identify areas where additional support or training is needed."
      },
      {
        title: "Cleaning Schedule",
        description:"Data-driven insights help deploy staff effectively across classes and duties, ensuring optimal coverage and balanced workloads. Automated shift notifications allow staff to stay informed of changes, reducing confusion and last-minute adjustments."
      },
      {
        title: "Staff Database",
        description:"Comprehensive staff profiles store personal information, qualifications, and employment history in a secure, centralised system. Role-based access controls ensure only authorised personnel can view or edit sensitive information."
      },
      {
        title: "Leave Management",
        description:"Staff can submit leave requests directly through the system, streamlining approval and record-keeping. Leave balances are automatically updated, helping managers plan resources without manual calculations."
      },
    ]
  },
  {
    id: "fee-management",
    title: "Seamless Fee Management",
    description: (
      <p>
        Qoqolo's fee management module streamlines the entire billing process, from setting up fee structures to generating invoices and receipts.
      </p>
    ),
    media: (
      <div className="productFeatures flex-row">
        <img src={require("~images/products/QQL/Features/6. Seamless fee management 1.jpg").default} alt="Seamless Fee Management" className="margin-auto img-responsive" />
        <img src={require("~images/products/QQL/Features/6. Seamless fee management 2.png").default} alt="Seamless Fee Management" className="margin-auto img-responsive" />
      </div>
    ),
    items:[
      {
        title: "Fee Templates",
        description:"Qoqolo allows schools to create and manage various fee categories tailored to different programmes or student groups. Fees can be assigned to individual students or groups with just a few clicks, ensuring accuracy and consistency across the system."
      },
      {
        title: "Invoices & Receipts",
        description:"Invoices and receipts can be generated automatically, reducing manual errors and saving valuable administrative time. The system also integrates payment options such as PayNow, making it easier for parents and guardians to settle fees promptly."
      },
      {
        title: "Reports",
        description:"The reporting tools provide comprehensive financial overviews, including fee collections, outstanding balances, and payment histories. Customisable filters allow administrators to generate specific insights by date, student, or fee category, supporting effective financial management."
      },
      {
        title: "Statement of Account",
        description:"Qoqolo generates clear statements of account that detail fees due, payments made, and outstanding amounts, providing transparency for parents and guardians. These statements can be easily downloaded for personal records or further processing."
      },
      {
        title: "Leave Management",
        description:"Staff can submit leave requests directly through the system, streamlining approval and record-keeping. Leave balances are automatically updated, helping managers plan resources without manual calculations."
      },
    ]
  },
  {
    id: "apps",
    title: "Mobile Phone and Tablet Apps",
    description: (
      <Fragment>
        <p>
          Parents, teachers and administrators can access our services through
          mobile phone and tablet apps, making it easy for everyone to stay
          informed. You can manage sign-in/out through our logbook tablet,
          ensuring accurate attendance records, while our mobile app lets
          parents stay up-to-date with their child&apos;s progress. The teacher
          app provides access to attendance reports, incident logs, student
          records and more, ensuring teachers have everything they need at
          their fingertips.
        </p>
        <div className="productFeatures flex-full">
          <div className="productApp">
            <img src={require("~images/products/QQL/Features/App/app-student.png").default} alt="Student App" className="margin-auto img-responsive" />
            <img src={require("~images/products/QQL/Features/App/app-school.png").default} alt="School App" className="margin-auto img-responsive" />
            <img src={require("~images/products/QQL/Features/App/app-teacher.png").default} alt="Teacher App" className="margin-auto img-responsive" />
            <img src={require("~images/products/QQL/Features/App/app-logbook.png").default} alt="Logbook App" className="margin-auto img-responsive" />
          </div>
          <p>
            <br />
            Find out more about{" "}
            <a href="https://www.qoqolo.com/features" title="Find out more about Qoqolo Apps" target="_blank" rel="noreferrer">
              Qoqolo Apps
            </a>
          </p>
          <div className="supportedDevices">
            <img src={require("~images/products/QQL/Features/App/supported_browser.jpg").default} alt="Supported Browsers" className="margin-auto img-responsive" />
            <img src={require("~images/products/QQL/Features/App/ios.png").default} alt="App Store" className="margin-auto img-responsive" />
            <img src={require("~images/products/QQL/Features/App/play.png").default} alt="Play Store" className="margin-auto img-responsive" />
          </div>
        </div>
      </Fragment>
    ),
  },
]

const faqSections = [
  {
    title: "What is Qoqolo's Parent Engagement Portal, and how does it benefit parents?",
    description: (
      <Fragment>
        <p>
          Qoqolo&apos;s Parent Engagement Portal is a user-friendly platform designed to foster seamless communication between parents and educators. It allows parents to:
        </p>
        <ul>
          <li>View real-time updates on their child&apos;s activities and progress.</li>
          <li>Access and download photos and videos of class events and learning moments.</li>
          <li>Participate in parent-teacher conferences and receive timely announcements.</li>
          <li>Monitor attendance and health records.</li>
        </ul>
        <p>
          This intuitive portal ensures that parents are actively involved in their child&apos;s educational journey, enhancing collaboration and support.
        </p>
      </Fragment>
    ),
  },
  {
    title: "How does Qoqolo's School Information Management System streamline administrative tasks?",
    description: (
      <Fragment>
        <p>
          Qoqolo's School Information Management System centralises all student data, enabling educators and administrators to:
        </p>
        <ul>
            <li>Store and access student information securely and efficiently.</li>
            <li>Track attendance, health records, and academic progress in real-time.</li>
            <li>Generate reports and analytics to inform decision-making.</li>
            <li>Ensure compliance with regulatory requirements through organised record-keeping.</li>
        </ul>
        <p>By automating routine tasks, the system reduces administrative workload, allowing staff to focus more on teaching and student development.</p>
      </Fragment>
    ),
  },
  {
    title: "What features does Qoqolo's School Management System offer to enhance daily operations?",
    description: (
      <Fragment>
        <p>
          Qoqolo's School Management System provides a comprehensive suite of tools to support daily school operations, including:
        </p>
        <ul>
          <li>Paperless management of staff information, including rosters and leave applications.</li>
          <li>Scheduling and tracking of staff training sessions.</li>
          <li>Fee management capabilities, such as invoicing and receipt generation.</li>
          <li>Centre operations management tools to streamline planning and development.</li>
        </ul>
        <p>These features contribute to a more organised and efficient school environment, promoting better resource management and operational effectiveness.</p>
      </Fragment>
    ),
  },
  {
    title: "Can Qoqolo's system be accessed on mobile devices?",
    description: (
      <Fragment>
        <p>
        Yes, Qoqolo offers mobile applications for parents, teachers, and administrators, ensuring accessibility on-the-go. These apps allow users to:
      </p>
      <ul>
        <li>Receive instant notifications and updates.</li>
        <li>Access and manage student information and records.</li>
        <li>Communicate with other users through integrated messaging features.</li>
      </ul>
      <p>The mobile-friendly design ensures that all stakeholders can stay connected and informed, regardless of location.</p>
      </Fragment>
    ),
  },
  {
    title: "How does Qoqolo support the creation and management of student portfolios?",
    description: (
      <Fragment>
        <p>
          Qoqolo simplifies the process of documenting and tracking student development through its ePortfolio feature. Educators can:
        </p>
        <ul>
          <li>Customise individual student portfolios with tags and observation notes.</li>
          <li>Utilise an auto-fill function to save time and ensure consistency.</li>
          <li>Generate progress reports with just a few clicks.</li>
        </ul>
        <p>This streamlined approach allows for comprehensive tracking of a child's learning journey, facilitating personalised education.</p>
      </Fragment>
    ),
  },
  {
    title: "Is Qoqolo's system suitable for various educational institutions?",
    description: (
      <p>Absolutely. Qoqolo is designed to cater to a wide range of educational settings, from preschools to primary schools. Its flexible and scalable solutions can be customised to meet the unique needs of different institutions, ensuring that all users benefit from its comprehensive features.</p>
    ),
  },
]

const customerLogos = [
  [require("~images/products/QQL/Customers/Aug_2026/1.pcf-logo_1.png").default, "PCF Sparkletots"],
  [require("~images/products/QQL/Customers/Aug_2026/2.starlearners-logo 1.png").default, "Star Learners"],
  [require("~images/products/QQL/Customers/Aug_2026/3.xingyou.png").default, "Chinese Kindergarten"],
  [require("~images/products/QQL/Customers/Aug_2026/4.Clementi Woods KLLP.png").default, "Clementi Woods Kindergarten LLP"],
  [require("~images/products/QQL/Customers/Aug_2026/5.Cornerstone.png").default, "Cornerstone Community Church"],
  [require("~images/products/QQL/Customers/Aug_2026/6.berries@2x.png").default, "Berries"],
  [require("~images/products/QQL/Customers/Aug_2026/7.Far Eastern.png").default, "Far Eastern Kindergarten"],
  [require("~images/products/QQL/Customers/Aug_2026/8.FinlandWay.png").default, "Finland Way"],
  [require("~images/products/QQL/Customers/Aug_2026/9.Kiddie Explorers Preschool.png").default, "Kiddie Explorers Preschool"],
  [require("~images/products/QQL/Customers/Aug_2026/10.Mamre Oasks.png").default, "Mamre Oaks"],
  [require("~images/products/QQL/Customers/Aug_2026/11.mosaic.png").default, "Mosaic"],
  [require("~images/products/QQL/Customers/Aug_2026/12.methodist presechool.png").default, "Methodist Preschool"],
  [require("~images/products/QQL/Customers/Aug_2026/13.Nurture Pods.png").default, "Nurture Pods"],
  [require("~images/products/QQL/Customers/Aug_2026/14.SAAC.png").default, "SAAC"],
  [require("~images/products/QQL/Customers/Aug_2026/15.SHHKPS.png").default, "SHHKPS"],
  [require("~images/products/QQL/Customers/Aug_2026/16.Orangetree.png").default, "Orange Tree Preschool"],
  [require("~images/products/QQL/Customers/Aug_2026/17.Sunshine Kid.png").default, "Sunshine Kids"],
  [require("~images/products/QQL/Customers/Aug_2026/18.jamiyah@2x.png").default, "Jamiyah"],
  [require("~images/products/QQL/Customers/Aug_2026/19.Pentecost.png").default, "Pentecost Methodist Church"],
]

const QoqoloPage = () => {
  const getItemId = (sectionId, itemTitle) =>
    `${sectionId}-${itemTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`

  const [openSections, setOpenSections] = useState(() => {
    const firstSectionWithItems = featureSections.find(section => section.items?.length)
    const firstFaq = faqSections[0]
    const initialIds = []

    if (firstSectionWithItems) {
      initialIds.push(getItemId(firstSectionWithItems.id, firstSectionWithItems.items[0].title))
    }

    if (firstFaq) {
      initialIds.push(getItemId("faq", firstFaq.title))
    }

    return initialIds
  })

  const toggleSection = id => {
    setOpenSections(current =>
      current.includes(id)
        ? current.filter(sectionId => sectionId !== id)
        : [...current, id]
    )
  }

  const renderAccordionDescription = description =>
    typeof description === "string" ? <p>{description}</p> : description

  const content = (
    <Fragment>
      <HeadingIcon>
        <a href="https://www.qoqolo.com/" target="_blank" rel="noopener noreferrer" title="Visit Qoqolo" ><img src={require("~images/products/qoqolo.png").default} alt="Visit Qoqolo" title="Visit Qoqolo" /></a>
      </HeadingIcon>
      <h1>Student Management System</h1>
      <p>Qoqolo is a comprehensive <strong>student management system software</strong> designed to streamline preschool operations in Singapore. It simplifies administrative tasks, enhances communication between educators and parents, and supports the holistic development of students. With features like real-time attendance tracking, health record management, and customisable ePortfolios, Qoqolo ensures that educators can focus more on teaching and less on paperwork. Its user-friendly interface and mobile accessibility make it an invaluable tool for modern educational institutions.</p>
      <br/>
      {featureSections.map((section, index) => (
        <div key={section.id}>
          {index === 0 ? <h2>{section.title}</h2> : <h2>{section.title}</h2>}
          {section.description}
          {section.media}

          {section.items?.length ? (
            <div className="product-accordion">
              {section.items.map(item => {
                const itemId = getItemId(section.id, item.title)
                const isOpen = openSections.includes(itemId)
                const panelId = `qoqolo-panel-${itemId}`

                return (
                  <div className={`product-accordion-item${isOpen ? " is-open" : ""}`} key={itemId}>
                    <button
                      type="button"
                      className="product-accordion-toggle"
                      onClick={() => toggleSection(itemId)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                    >
                      <h3>{item.title}</h3>
                      <span className="product-accordion-symbol" aria-hidden="true" />
                    </button>

                  <div id={panelId} className={`product-accordion-panel${isOpen ? " is-open" : ""}`}>
                      {renderAccordionDescription(item.description)}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      ))}

      <h2>Experience the Qoqolo Advantage</h2>
      <p>
        Qoqolo&apos;s School Management System is designed to cater to all your
        school&apos;s needs. With multilingual support, intuitive interfaces, and
        mobile apps for parents and teachers, we provide the ultimate solution
        for your school&apos;s digital transformation. Try Qoqolo today and
        experience the ease and convenience of our smart companion for your
        preschool!
      </p>
      <br />
      <h2>Our Education Partners</h2>
      <div className="product-customers">
        {customerLogos.map(([src, alt]) => (
          <img
            key={alt}
            src={src}
            alt={alt}
            className="margin-auto img-responsive"
          />
        ))}
      </div>
      <br />
      <p className="text-center">
        <a href="https://www.qoqolo.com" title="Visit Qoqolo">
          <img src={require("~images/products/qoqolo.png").default} alt="Qoqolo" title="Qoqolo" className="margin-auto img-responsive block" />
          Visit Qoqolo
        </a>
      </p>
      <h2 className="contactus-info">Contact Us</h2>
      <p>
        For more information or sales enquiries, email us at{" "}
        <a href="mailto:biz@commontown.com">biz@commontown.com</a> or call us
        at <a href="tel:6848-8900">6848-8900</a>.
      </p>
      <br />
      <h2>Frequently Asked Questions</h2>
      <div className="product-accordion">
        {faqSections.map(item => {
          const itemId = getItemId("faq", item.title)
          const isOpen = openSections.includes(itemId)
          const panelId = `qoqolo-panel-${itemId}`

          return (
            <div className={`product-accordion-item${isOpen ? " is-open" : ""}`} key={itemId}>
              <button
                type="button"
                className="product-accordion-toggle"
                onClick={() => toggleSection(itemId)}
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <h3>{item.title}</h3>
                <span className="product-accordion-symbol" aria-hidden="true" />
              </button>

              <div id={panelId} className={`product-accordion-panel${isOpen ? " is-open" : ""}`}>
                {renderAccordionDescription(item.description)}
              </div>
            </div>
          )
        })}
      </div>
    </Fragment>
  )

  return <Sub title="Qoqolo" seo_name="Qoqolo" crumbs={crumbs} content={content} aside={aside} />
}

export default QoqoloPage
